import { inject, Service } from '@angular/core';
import { Store } from '@ngxs/store';
import { ProfileState, ProfileStateSetAppDeviceId } from '@lexplus-shared-lib';

@Service()
export class AppDeviceService {
  private readonly store = inject(Store);

  constructor() {
    this.initializeDeviceId();
  }

  /**
   * Returns the device's unique ID from the state.
   */
  public getDeviceId(): string {
    return this.store.selectSnapshot(ProfileState.appDeviceId) || '';
  }

  /**
   * Resets the device ID (generates a new one).
   */
  public resetDeviceId(): void {
    const newDeviceId = this.generateUniqueId();
    this.store.dispatch(new ProfileStateSetAppDeviceId(newDeviceId));
  }

  /**
   * Clears the device ID from the state.
   */
  public clearDeviceId(): void {
    this.store.dispatch(new ProfileStateSetAppDeviceId(''));
  }

  /**
   * Initializes the device ID by generating it and storing it in the state.
   */
  private initializeDeviceId(): void {
    // Check whether an ID already exists in the state.
    const existingDeviceId = this.store.selectSnapshot(ProfileState.appDeviceId);

    if (!existingDeviceId) {
      const newDeviceId = this.generateUniqueId();
      this.store.dispatch(new ProfileStateSetAppDeviceId(newDeviceId));
    }
  }

  /**
   * Generates a unique ID based on the device properties.
   */
  private generateUniqueId(): string {
    const timestamp = Date.now().toString(36);
    const randomPart = Math.random().toString(36).substring(2, 15);
    const userAgent = this.getUserAgentHash();
    const screenInfo = this.getScreenInfoHash();

    return `${timestamp}-${randomPart}-${userAgent}-${screenInfo}`;
  }

  /**
   * Creates a hash based on the user agent.
   */
  private getUserAgentHash(): string {
    const userAgent = navigator.userAgent;
    return this.simpleHash(userAgent);
  }

  /**
   * Creates a hash based on the screen information.
   */
  private getScreenInfoHash(): string {
    const screenInfo = `${window.screen.width}x${window.screen.height}x${window.screen.colorDepth}`;
    return this.simpleHash(screenInfo);
  }

  /**
   * Simple hash function that produces a short string.
   */
  private simpleHash(str: string): string {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash;
    }
    return Math.abs(hash).toString(36).substring(0, 8);
  }
}
